import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sun, MapPin, Camera, Compass, LogOut, Menu, X, Plus, Trash2,
  Aperture, Focus, Cpu, Battery, Layers, Box
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'camera', labelKey: 'equipment.camera', icon: Camera },
  { value: 'lens', labelKey: 'equipment.lens', icon: Aperture },
  { value: 'tripod', labelKey: 'equipment.tripod', icon: Focus },
  { value: 'filter', labelKey: 'equipment.filter', icon: Layers },
  { value: 'drone', labelKey: 'equipment.drone', icon: Cpu },
  { value: 'accessory', labelKey: 'equipment.accessory', icon: Battery },
  { value: 'other', labelKey: 'equipment.other', icon: Box },
];

const getCategoryIcon = (category) => {
  const cat = CATEGORIES.find(c => c.value === category);
  return cat ? cat.icon : Box;
};

const getCategoryLabelKey = (category) => {
  const cat = CATEGORIES.find(c => c.value === category);
  return cat ? cat.labelKey : 'equipment.other';
};

const EquipmentPage = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [equipment, setEquipment] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    name: '',
    category: 'camera',
    notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API_URL}/equipment`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEquipment = async () => {
    if (!newEquipment.name.trim()) {
      toast.error(t('equipment.nameRequired'));
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/equipment`, newEquipment);
      setEquipment([...equipment, response.data]);
      setIsDialogOpen(false);
      setNewEquipment({ name: '', category: 'camera', notes: '' });
      toast.success(t('equipment.addedSuccess'));
    } catch (error) {
      toast.error(t('equipment.addError'));
    }
  };

  const handleDeleteEquipment = async (eqId) => {
    try {
      await axios.delete(`${API_URL}/equipment/${eqId}`);
      setEquipment(equipment.filter(e => e.id !== eqId));
      toast.success(t('equipment.deletedSuccess'));
    } catch (error) {
      toast.error(t('equipment.deleteError'));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredEquipment = filterCategory === 'all' 
    ? equipment 
    : equipment.filter(e => e.category === filterCategory);

  const groupedEquipment = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = filteredEquipment.filter(e => e.category === cat.value);
    return acc;
  }, {});

  return (
    <div className="h-screen bg-slate-950 flex overflow-hidden">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            className="w-[280px] bg-slate-900/50 border-r border-white/10 flex flex-col z-20"
          >
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg gradient-golden-hour flex items-center justify-center">
                  <Sun className="w-6 h-6 text-black" />
                </div>
                <span className="font-heading font-semibold text-xl text-white">LuminaSpot</span>
              </div>
            </div>

            <nav className="flex-1 p-4 space-y-2">
              <Link to="/dashboard">
                <Button variant="ghost" className="w-full justify-start gap-3 text-slate-300 hover:text-white hover:bg-white/5" data-testid="nav-dashboard">
                  <Compass className="w-5 h-5" />
                  {t('nav.planner')}
                </Button>
              </Link>
              <Link to="/spots">
                <Button variant="ghost" className="w-full justify-start gap-3 text-slate-300 hover:text-white hover:bg-white/5" data-testid="nav-spots">
                  <MapPin className="w-5 h-5" />
                  {t('nav.spots')}
                </Button>
              </Link>
              <Link to="/equipment">
                <Button variant="ghost" className="w-full justify-start gap-3 text-amber-500 bg-amber-500/10" data-testid="nav-equipment">
                  <Camera className="w-5 h-5" />
                  {t('nav.equipment')}
                </Button>
              </Link>
            </nav>

            <div className="p-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                    <span className="text-amber-500 font-medium">{user?.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{user?.name}</p>
                    <p className="text-slate-500 text-xs">{user?.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="logout-btn">
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-slate-900/50 border-b border-white/10 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <h1 className="font-heading font-semibold text-xl text-white">{t('equipment.title')}</h1>
          </div>

          <div className="flex items-center gap-4">
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] bg-slate-900 border-white/10" data-testid="category-filter">
                <SelectValue placeholder={t('equipment.filterByCategory')} />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                <SelectItem value="all">{t('equipment.allCategories')}</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{t(cat.labelKey)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider text-sm btn-tactile" data-testid="add-equipment-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('equipment.addEquipment')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-white/10">
                <DialogHeader>
                  <DialogTitle className="font-heading text-white">{t('equipment.addEquipment')}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('equipment.name')}</Label>
                    <Input
                      value={newEquipment.name}
                      onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                      placeholder={t('equipment.namePlaceholder')}
                      className="bg-slate-800 border-white/10"
                      data-testid="equipment-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('equipment.category')}</Label>
                    <Select
                      value={newEquipment.category}
                      onValueChange={(value) => setNewEquipment({ ...newEquipment, category: value })}
                    >
                      <SelectTrigger className="bg-slate-800 border-white/10" data-testid="equipment-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10">
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <div className="flex items-center gap-2">
                              <cat.icon className="w-4 h-4" />
                              {t(cat.labelKey)}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('equipment.notes')}</Label>
                    <Textarea
                      value={newEquipment.notes}
                      onChange={(e) => setNewEquipment({ ...newEquipment, notes: e.target.value })}
                      placeholder={t('equipment.notesPlaceholder')}
                      className="bg-slate-800 border-white/10"
                      data-testid="equipment-notes-input"
                    />
                  </div>

                  <Button
                    onClick={handleCreateEquipment}
                    className="w-full bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider btn-tactile"
                    data-testid="save-equipment-btn"
                  >
                    {t('equipment.addEquipment')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className="text-center py-20">
              <Camera className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-heading text-white mb-2">{t('equipment.noEquipmentTitle')}</h3>
              <p className="text-slate-400 mb-6">{t('equipment.noEquipmentSubtitle')}</p>
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider btn-tactile"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('equipment.addFirstItem')}
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {CATEGORIES.map(cat => {
                const items = groupedEquipment[cat.value];
                if (items.length === 0 && filterCategory !== 'all') return null;
                if (items.length === 0) return null;
                
                return (
                  <div key={cat.value}>
                    <h2 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
                      <cat.icon className="w-5 h-5 text-amber-500" />
                      {t(cat.labelKey)}
                      <span className="text-slate-500 text-sm font-normal">({items.length})</span>
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {items.map(item => {
                        const Icon = getCategoryIcon(item.category);
                        return (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            <Card className="bg-slate-900/50 border-white/10 hover:border-amber-500/30 transition-all card-interactive" data-testid={`equipment-card-${item.id}`}>
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                                      <Icon className="w-5 h-5 text-amber-500" />
                                    </div>
                                    <div className="min-w-0">
                                      <h3 className="font-medium text-white truncate">{item.name}</h3>
                                      <p className="text-xs text-slate-500">{t(getCategoryLabelKey(item.category))}</p>
                                      {item.notes && (
                                        <p className="text-sm text-slate-400 mt-2 line-clamp-2">{item.notes}</p>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteEquipment(item.id)}
                                    className="text-slate-500 hover:text-red-500 flex-shrink-0"
                                    data-testid={`delete-equipment-${item.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentPage;
